/**
 * Step 2 — About you (applicant identity).
 *
 * Per `rcp-apply-now-step2-applicant.html`.
 *
 * A11y fixes:
 *   - `autocomplete` on every input (WCAG 1.3.5).
 *   - SSN input MASKS but does not swallow backspace (see maskSsn fn).
 *   - Explicit error text via `aria-describedby`.
 *   - SSN masked to `***-**-1234` in any surfacing (never in URL/log).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useApplyWizard } from './ApplyWizard';
import { useToast } from '../../components/a11y/AccessibleToast';
import { getApiClient } from '../../services/apiClient';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import type { PersonDraft } from '@retail-ops/shared-types';

/**
 * Formats as XXX-XX-XXXX as the user types. Preserves backspace (never refills
 * deleted digits). Returns only formatted display — raw digits stored separately.
 */
function maskSsn(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

export function Step2Applicant(): JSX.Element {
  const { draftId, gotoStep } = useApplyWizard();
  const { pushToast } = useToast();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [ssnMasked, setSsnMasked] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!firstName.trim()) errs['firstName'] = 'First name is required.';
    if (!lastName.trim()) errs['lastName'] = 'Last name is required.';
    if (!dob) errs['dob'] = 'Date of birth is required.';
    if (!ssnMasked || ssnMasked.replace(/\D/g, '').length !== 9) errs['ssn'] = 'Enter your 9-digit Social Security number.';
    if (!street.trim()) errs['street'] = 'Street address is required.';
    if (!city.trim()) errs['city'] = 'City is required.';
    if (state.length !== 2) errs['state'] = 'Choose your state.';
    if (!/^\d{5}(-\d{4})?$/.test(zip)) errs['zip'] = 'Enter a valid ZIP code (e.g. 65401).';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function saveAndContinue(): Promise<void> {
    if (!validate()) {
      pushToast('error', 'Check the highlighted fields', 'Some information needs your attention.');
      return;
    }
    if (!draftId) {
      pushToast('warn', 'Your session needs to restart', 'Please start over from step 1.');
      gotoStep(0);
      return;
    }
    setSubmitting(true);
    try {
      const digits = ssnMasked.replace(/\D/g, '');
      const lastFour = digits.slice(-4);
      const payload: PersonDraft = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        dob,
        // SSN is never sent in its raw form in URL or log.
        ssnMasked: `***-**-${lastFour}`,
        address: { streetAddress: street.trim(), city: city.trim(), state, zip },
      };
      const client = getApiClient();
      // TODO(PLANNED /portal/apply/save-person-draft): server persists PersonDraft.
      const res = await client.portal.apply.savePersonDraft(draftId, payload);
      if (res.success) {
        navigate('/apply/cip-id');
      } else {
        pushToast('error', 'We could not save your information', res.error ?? 'Please try again.');
      }
    } catch {
      pushToast('error', 'Network error', 'Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section aria-labelledby="step2-heading">
      <h1 id="step2-heading" tabIndex={-1}>Tell us about you</h1>
      <p className="lead" style={{ marginBottom: 24 }}>
        We need some basic info to verify your identity. This is required by federal law.
      </p>
      <div className="card">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="field">
            <label htmlFor="firstName">First name</label>
            <input
              id="firstName"
              className="input"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              aria-invalid={errors['firstName'] ? true : undefined}
              aria-describedby={errors['firstName'] ? 'firstName-err' : undefined}
              required
            />
            {errors['firstName'] ? <span id="firstName-err" className="error">{errors['firstName']}</span> : null}
          </div>
          <div className="field">
            <label htmlFor="lastName">Last name</label>
            <input
              id="lastName"
              className="input"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              aria-invalid={errors['lastName'] ? true : undefined}
              aria-describedby={errors['lastName'] ? 'lastName-err' : undefined}
              required
            />
            {errors['lastName'] ? <span id="lastName-err" className="error">{errors['lastName']}</span> : null}
          </div>
          <div className="field">
            <label htmlFor="dob">Date of birth</label>
            <input
              id="dob"
              type="date"
              className="input"
              autoComplete="bday"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              aria-invalid={errors['dob'] ? true : undefined}
              aria-describedby={errors['dob'] ? 'dob-err' : undefined}
              required
            />
            {errors['dob'] ? <span id="dob-err" className="error">{errors['dob']}</span> : null}
          </div>
          <div className="field">
            <label htmlFor="ssn">Social Security number</label>
            <input
              id="ssn"
              className="input mono"
              inputMode="numeric"
              autoComplete="off"
              value={ssnMasked}
              onChange={(e) => setSsnMasked(maskSsn(e.target.value))}
              placeholder="XXX-XX-XXXX"
              aria-invalid={errors['ssn'] ? true : undefined}
              aria-describedby={errors['ssn'] ? 'ssn-err ssn-hint' : 'ssn-hint'}
              required
            />
            <span id="ssn-hint" className="hint">9 digits, formatted automatically.</span>
            {errors['ssn'] ? <span id="ssn-err" className="error">{errors['ssn']}</span> : null}
          </div>
        </div>
      </div>

      <fieldset className="card" style={{ border: '1px solid var(--surface-border)' }}>
        <legend style={{ fontWeight: 500, color: 'var(--pcb-blue-dark)', padding: '0 8px' }}>Home address</legend>
        <div className="field">
          <label htmlFor="street">Street address</label>
          <input
            id="street" className="input"
            autoComplete="street-address"
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            aria-invalid={errors['street'] ? true : undefined}
            aria-describedby={errors['street'] ? 'street-err' : undefined}
            required
          />
          {errors['street'] ? <span id="street-err" className="error">{errors['street']}</span> : null}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 16 }}>
          <div className="field">
            <label htmlFor="city">City</label>
            <input
              id="city" className="input"
              autoComplete="address-level2"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              aria-invalid={errors['city'] ? true : undefined}
              aria-describedby={errors['city'] ? 'city-err' : undefined}
              required
            />
            {errors['city'] ? <span id="city-err" className="error">{errors['city']}</span> : null}
          </div>
          <div className="field">
            <label htmlFor="state">State</label>
            <input
              id="state" className="input"
              autoComplete="address-level1"
              maxLength={2}
              value={state}
              onChange={(e) => setState(e.target.value.toUpperCase())}
              aria-invalid={errors['state'] ? true : undefined}
              aria-describedby={errors['state'] ? 'state-err' : undefined}
              required
            />
            {errors['state'] ? <span id="state-err" className="error">{errors['state']}</span> : null}
          </div>
          <div className="field">
            <label htmlFor="zip">ZIP</label>
            <input
              id="zip" className="input mono"
              autoComplete="postal-code"
              inputMode="numeric"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              aria-invalid={errors['zip'] ? true : undefined}
              aria-describedby={errors['zip'] ? 'zip-err' : undefined}
              required
            />
            {errors['zip'] ? <span id="zip-err" className="error">{errors['zip']}</span> : null}
          </div>
        </div>
      </fieldset>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', marginTop: 16 }}>
        <button type="button" className="btn btn-ghost" onClick={() => gotoStep(0)}>
          <ArrowLeft aria-hidden="true" size={16} /> Back
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void saveAndContinue()}
          disabled={submitting}
          aria-busy={submitting || undefined}
        >
          {submitting ? 'Saving...' : 'Continue'}
          <ArrowRight aria-hidden="true" size={16} />
        </button>
      </div>
    </section>
  );
}
